/** Shared Tailwind class strings for the few primitives used across pages.
 *  Keeping them here (not in CSS) means every style is a utility and the
 *  design tokens live in one place: `@theme` in index.css. */

/** Chart series colours (`--chart-N` in index.css, light and dark). A
 *  comparison gives each selected store one; `MAX_COMPARE` matches. */
export const chartSeries = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--chart-6)",
] as const;

const btnBase =
  "inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border px-3.5 py-2 font-[550] transition-colors duration-100 disabled:cursor-not-allowed disabled:opacity-50";

/** In dark mode the accent is a light blue, so filled controls flip to dark
 *  ink for contrast. */
const onAccent = "text-white dark:text-[#0c1626]";

export const ui = {
  /** Elevated surface — panels, stat cards, toolbars. */
  card: "rounded-card border border-border bg-surface shadow-[0_1px_2px_0_rgb(16_24_40_/_0.05)]",

  btn: `${btnBase} ${onAccent} border-accent bg-accent shadow-[0_1px_2px_0_rgb(16_24_40_/_0.1)] enabled:hover:border-[color-mix(in_srgb,var(--accent)_85%,black)] enabled:hover:bg-[color-mix(in_srgb,var(--accent)_85%,black)] dark:enabled:hover:border-[color-mix(in_srgb,var(--accent)_88%,white)] dark:enabled:hover:bg-[color-mix(in_srgb,var(--accent)_88%,white)]`,
  btnQuiet: `${btnBase} border-border bg-surface text-text enabled:hover:border-accent enabled:hover:bg-[color-mix(in_srgb,var(--accent)_8%,transparent)]`,
  btnDanger: `${btnBase} border-border bg-surface text-danger enabled:hover:border-danger enabled:hover:bg-[color-mix(in_srgb,var(--danger)_8%,transparent)]`,
  /** Toggle-style quiet button in its "on" state. */
  btnSelected:
    "border-accent! text-accent! bg-[color-mix(in_srgb,var(--accent)_12%,transparent)]!",

  chip: "cursor-pointer rounded-full border border-border bg-surface px-3 py-[5px] text-[13px] text-text transition-colors duration-100 hover:border-accent hover:bg-[color-mix(in_srgb,var(--accent)_8%,transparent)]",
  chipOn: "border-accent! bg-accent! text-white! dark:text-[#0c1626]!",

  pill: "inline-block rounded-full border border-border bg-bg px-[9px] py-0.5 text-xs capitalize",
  pillRunning:
    "border-[color-mix(in_srgb,var(--accent)_35%,transparent)]! bg-[color-mix(in_srgb,var(--accent)_10%,transparent)]! text-accent",
  pillSucceeded:
    "border-[color-mix(in_srgb,var(--success)_35%,transparent)]! bg-[color-mix(in_srgb,var(--success)_10%,transparent)]! text-success",
  pillFailed:
    "border-[color-mix(in_srgb,var(--danger)_35%,transparent)]! bg-[color-mix(in_srgb,var(--danger)_10%,transparent)]! text-danger",

  notice: "mb-3 rounded-lg border border-border bg-bg px-3 py-2.5 text-[13px]",
  noticeError:
    "border-[color-mix(in_srgb,var(--danger)_35%,transparent)]! bg-[color-mix(in_srgb,var(--danger)_7%,transparent)]! text-danger",
  noticeSuccess:
    "border-[color-mix(in_srgb,var(--success)_35%,transparent)]! bg-[color-mix(in_srgb,var(--success)_7%,transparent)]! text-success",

  /** Small uppercase caption (filter labels, stat labels). */
  label: "text-xs font-semibold uppercase tracking-[0.6px] text-muted",
  /** `<label>` wrapping a caption + input. */
  field: "flex flex-col gap-1.5 text-xs font-semibold text-muted",

  /** Text-like inputs (text, search, date, number). */
  input:
    "rounded-lg border border-border bg-surface px-2.5 py-[7px] text-sm font-normal text-text placeholder:text-muted focus:border-accent-2 focus:outline-none focus:ring-2 focus:ring-accent-2/25 disabled:cursor-not-allowed disabled:opacity-60",
  /** Native `<select>` — used by `components/Select.tsx`, which adds the chevron. */
  select:
    "w-full cursor-pointer appearance-none rounded-lg border border-border bg-surface py-[7px] pr-8 pl-2.5 text-sm font-normal text-text focus:border-accent-2 focus:outline-none focus:ring-2 focus:ring-accent-2/25 disabled:cursor-not-allowed disabled:opacity-60",

  /** Filter/control strip inside a panel — sits on the card, tinted like the page. */
  toolbar:
    "flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-border bg-bg px-4 py-3",

  table:
    "w-full border-collapse text-[13px] [&_th]:border-b [&_th]:border-border [&_th]:pb-2 [&_th]:pr-3 [&_th]:text-left [&_th]:text-[11px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-[0.6px] [&_th]:text-muted [&_td]:border-b [&_td]:border-border [&_td]:py-2.5 [&_td]:pr-3 [&_td]:align-top [&_tbody_tr:hover]:bg-[color-mix(in_srgb,var(--accent)_3%,transparent)]",

  /** `<details>` with a one-word summary and a scrollable `<pre>`. */
  details:
    "[&>summary]:cursor-pointer [&>summary]:text-xs [&>summary]:text-danger [&>pre]:max-h-[180px] [&>pre]:max-w-[380px] [&>pre]:overflow-auto [&>pre]:rounded-md [&>pre]:bg-bg [&>pre]:p-2 [&>pre]:text-[11px]",
  detailsNeutral: "[&>summary]:text-muted!",
} as const;
